/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import type {
  TContentRating,
  TRatingTaxonomy,
  TRatingTypeEntry,
  TRatingValueEntry
} from "../types";

/**
 * Resolved facts about one rating value, used by the rule engine.
 */
export interface TRatingValueInfo {
  /**
   * True when the value is in the adult category (DRD 2.3, WEB-PC-033).
   */
  isAdult: boolean;

  /**
   * True when the value is excluded from the Rating Limit selector.
   */
  isHidden: boolean;

  /**
   * True for the "not rated" values NR and TV-NR. Under WEB-PC-015 they are
   * rated values with their own level, which the service must supply (OQ-19).
   */
  isNotRated: boolean;

  /**
   * Restrictiveness, higher is stricter; null when it cannot be determined.
   */
  level: number | null;

  /**
   * Display name from the taxonomy. Never hard-coded in the UI.
   */
  name: string;

  /**
   * Normalised rating type ID.
   */
  ratingTypeId: string;

  /**
   * Normalised rating value ID.
   */
  ratingValueId: string;
}

/**
 * Resolved facts about one rating type (rating system).
 */
export interface TRatingTypeInfo {
  /**
   * Service level at or above which a value of this type is adult, or null.
   */
  adultLevel: number | null;

  /**
   * Default unrated policy for this type, or null when not delivered.
   */
  blockUnratedPrograms: boolean | null;

  /**
   * Position of this type's Rating Limit selector, or null when not delivered.
   */
  displayRank: number | null;

  /**
   * Display name from the taxonomy, e.g. `movie rating`.
   */
  name: string;

  /**
   * Normalised rating type ID.
   */
  ratingTypeId: string;
}

/**
 * Interim restrictiveness per rating type, keyed by upper-cased display name,
 * in the audience order of DRD 2.3. Used only for a type whose values carry
 * no service `level` (absent from the generated client until OQ-3 closes).
 * Value IDs are not in audience order (TV-Y7 is rv.1, TV-Y is rv.2), so names
 * are used (WEB-PC-012). NR and TV-NR are absent on purpose: their level must
 * come from the service (OQ-19). Delete once the service returns `level`.
 */
const INTERIM_LEVELS: Readonly<
  Record<string, Readonly<Record<string, number>>>
> = {
  "tivo:rt.1": {
    G: 10,
    PG: 20,
    "PG-13": 30,
    R: 40,
    "NC-17": 50,
    // WEB-PC-014: these share the highest movie rank.
    AO: 60,
    "NR-ADULT": 60,
    X: 60,
    XX: 60,
    XXX: 60
  },
  "tivo:rt.2": {
    "TV-Y": 10,
    "TV-Y7": 20,
    "TV-G": 30,
    "TV-PG": 40,
    "TV-14": 50,
    "TV-MA": 60
  },
  "tivo:rt.6": {
    C: 10,
    C8: 20,
    G: 30,
    PG: 40,
    "14+": 50,
    "18+": 60,
    ADULT: 70
  },
  "tivo:rt.7": {
    GENERAL: 10,
    "+12": 20,
    "+18": 30,
    ADULT: 40,
    ADULTO: 40
  }
};

/** Upper-cased names that mean "not rated". `NR-Adult` is deliberately absent. */
const NOT_RATED_NAMES: ReadonlySet<string> = new Set<string>(["NR", "TV-NR"]);

/** Upper-cased adult names, used when `adultLevel` cannot be applied. */
const ADULT_NAMES: ReadonlySet<string> = new Set<string>([
  "AO",
  "NR-ADULT",
  "X",
  "XX",
  "XXX",
  "ADULT",
  "ADULTO"
]);

/** An empty, frozen value list shared by every lookup that finds nothing. */
const NO_VALUES: ReadonlyArray<TRatingValueInfo> = Object.freeze([]);

/**
 * Read a finite number.
 * @param value - Raw value
 * @returns The number, or null when absent or not finite
 */
function toFiniteNumber(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

/**
 * Read a display name.
 * @param value - Raw value
 * @returns The trimmed name, or an empty string
 */
function toName(value: unknown): string {
  return typeof value === "string" ? value.trim() : "";
}

/**
 * Keep only the object entries of a response array, so a corrupted cache
 * cannot throw during indexing.
 * @param list - Raw array
 * @returns Object entries, or an empty array
 */
function toEntries<T>(list: unknown): ReadonlyArray<T> {
  if (!Array.isArray(list)) {
    return [];
  }
  return (list as Array<unknown>).filter(
    (entry: unknown): boolean => entry !== null && typeof entry === "object"
  ) as Array<T>;
}

/**
 * Sort by a nullable number, nulls last, ties in response order.
 * @param items - Items with their response index
 * @param rank - Rank accessor
 * @returns The items, sorted and frozen
 */
function sortByRank<T>(
  items: ReadonlyArray<{ index: number; item: T }>,
  rank: (item: T) => number | null
): ReadonlyArray<T> {
  const sorted: Array<{ index: number; item: T }> = [...items].sort(
    (a: { index: number; item: T }, b: { index: number; item: T }): number => {
      const rankA: number | null = rank(a.item);
      const rankB: number | null = rank(b.item);
      if (rankA !== rankB) {
        if (rankA === null) {
          return 1;
        }
        if (rankB === null) {
          return -1;
        }
        return rankA - rankB;
      }
      return a.index - b.index;
    }
  );
  return Object.freeze(
    sorted.map((entry: { index: number; item: T }): T => entry.item)
  );
}

/**
 * Indexed, normalised view of the rating taxonomy from
 * `GET /v1/internalRatings` (DRD 2.3).
 *
 * Content and limits carry only IDs; this class gives each ID a type, a
 * display name, a restrictiveness level and its adult and not-rated
 * classification. Everything is indexed once at construction, so every
 * lookup is O(1) and the instance is immutable.
 */
export class RatingTaxonomy {
  /** Movie rating type — wins precedence when movie and TV both exceed. */
  public static readonly MOVIE_RATING_TYPE_ID: string = "tivo:rt.1";

  /** TV rating type. */
  public static readonly TV_RATING_TYPE_ID: string = "tivo:rt.2";

  /** Declared rating types keyed by normalised type ID. */
  private readonly mTypes: Map<string, TRatingTypeInfo>;

  /** Declared rating types ordered by `displayRank`. */
  private readonly mOrderedTypes: ReadonlyArray<TRatingTypeInfo>;

  /** Rating values keyed by {@link RatingTaxonomy.toKey}. */
  private readonly mValues: Map<string, TRatingValueInfo>;

  /** Every delivered type ID: declared, or referenced by a value. */
  private readonly mDeliveredTypeIds: Set<string>;

  /** Per type, the values with a known level, least restrictive first. */
  private readonly mOrderedValues: Map<string, ReadonlyArray<TRatingValueInfo>>;

  /** Per type, {@link mOrderedValues} without hidden values. */
  private readonly mSelectableValues: Map<
    string,
    ReadonlyArray<TRatingValueInfo>
  >;

  /**
   * Build the index from a taxonomy response.
   * @param taxonomy - Response of `GET /v1/internalRatings`, fresh or cached
   */
  public constructor(taxonomy: TRatingTaxonomy) {
    this.mTypes = new Map<string, TRatingTypeInfo>();
    this.mValues = new Map<string, TRatingValueInfo>();
    this.mDeliveredTypeIds = new Set<string>();
    this.mOrderedValues = new Map<string, ReadonlyArray<TRatingValueInfo>>();
    this.mSelectableValues = new Map<string, ReadonlyArray<TRatingValueInfo>>();

    const typeEntries: ReadonlyArray<TRatingTypeEntry> = toEntries(
      taxonomy !== null && typeof taxonomy === "object"
        ? taxonomy.ratingType
        : undefined
    );
    const valueEntries: ReadonlyArray<TRatingValueEntry> = toEntries(
      taxonomy !== null && typeof taxonomy === "object"
        ? taxonomy.ratingValue
        : undefined
    );

    this.indexTypes(typeEntries);
    this.mOrderedTypes = sortByRank(
      Array.from(this.mTypes.values()).map(
        (item: TRatingTypeInfo, index: number) => ({ index, item })
      ),
      (type: TRatingTypeInfo): number | null => type.displayRank
    );
    this.indexValues(valueEntries);
    this.orderValues();
  }

  /**
   * Index the declared rating types. A repeated type ID keeps the last entry.
   * @param entries - `ratingType[]` entries
   */
  private indexTypes(entries: ReadonlyArray<TRatingTypeEntry>): void {
    for (const entry of entries) {
      const ratingTypeId: string = RatingTaxonomy.normalizeId(
        entry.ratingTypeId
      );
      if (ratingTypeId === "") {
        continue;
      }
      this.mDeliveredTypeIds.add(ratingTypeId);
      this.mTypes.set(
        ratingTypeId,
        Object.freeze({
          adultLevel: toFiniteNumber(entry.adultLevel),
          blockUnratedPrograms:
            typeof entry.blockUnratedPrograms === "boolean"
              ? entry.blockUnratedPrograms
              : null,
          displayRank: toFiniteNumber(entry.displayRank),
          name: toName(entry.name),
          ratingTypeId
        })
      );
    }
  }

  /**
   * Index the rating values. A repeated type/value pair keeps the last entry.
   * @param entries - `ratingValue[]` entries
   */
  private indexValues(entries: ReadonlyArray<TRatingValueEntry>): void {
    // A type with any service level never mixes in interim levels, so the two
    // scales are never compared.
    const typesWithServiceLevels: Set<string> = new Set<string>();
    for (const entry of entries) {
      if (toFiniteNumber(entry.level) !== null) {
        typesWithServiceLevels.add(
          RatingTaxonomy.normalizeId(entry.ratingTypeId)
        );
      }
    }
    for (const entry of entries) {
      const ratingTypeId: string = RatingTaxonomy.normalizeId(
        entry.ratingTypeId
      );
      const ratingValueId: string = RatingTaxonomy.normalizeId(
        entry.ratingValueId
      );
      if (ratingTypeId === "" || ratingValueId === "") {
        continue;
      }
      this.mDeliveredTypeIds.add(ratingTypeId);
      this.mValues.set(
        RatingTaxonomy.toKey(ratingTypeId, ratingValueId),
        this.resolveValue(
          ratingTypeId,
          ratingValueId,
          entry,
          typesWithServiceLevels.has(ratingTypeId)
        )
      );
    }
  }

  /**
   * Build the per-type level order used by the engine and the selector.
   */
  private orderValues(): void {
    const byType: Map<
      string,
      Array<{ index: number; item: TRatingValueInfo }>
    > = new Map<string, Array<{ index: number; item: TRatingValueInfo }>>();
    let index: number = 0;
    for (const info of this.mValues.values()) {
      if (info.level !== null) {
        let list: Array<{ index: number; item: TRatingValueInfo }> | undefined =
          byType.get(info.ratingTypeId);
        if (list === undefined) {
          list = [];
          byType.set(info.ratingTypeId, list);
        }
        list.push({ index, item: info });
      }
      index += 1;
    }
    for (const [ratingTypeId, list] of byType) {
      const ordered: ReadonlyArray<TRatingValueInfo> = sortByRank(
        list,
        (info: TRatingValueInfo): number | null => info.level
      );
      this.mOrderedValues.set(ratingTypeId, ordered);
      this.mSelectableValues.set(
        ratingTypeId,
        Object.freeze(
          ordered.filter((info: TRatingValueInfo): boolean => !info.isHidden)
        )
      );
    }
  }

  /**
   * Work out level, not-rated and adult facts for one rating value.
   * @param ratingTypeId - Normalised rating type ID
   * @param ratingValueId - Normalised rating value ID
   * @param entry - Taxonomy entry
   * @param isServiceLevelType - True when the type's levels come from the
   *   service, so the interim table must not be used
   * @returns Resolved, frozen facts
   */
  private resolveValue(
    ratingTypeId: string,
    ratingValueId: string,
    entry: TRatingValueEntry,
    isServiceLevelType: boolean
  ): TRatingValueInfo {
    const name: string = toName(entry.name);
    const upperName: string = name.toUpperCase();
    const serviceLevel: number | null = toFiniteNumber(entry.level);
    const level: number | null = isServiceLevelType
      ? serviceLevel
      : RatingTaxonomy.interimLevel(ratingTypeId, upperName);
    const type: TRatingTypeInfo | undefined = this.mTypes.get(ratingTypeId);
    const adultLevel: number | null =
      type !== undefined ? type.adultLevel : null;
    // `adultLevel` is on the service scale, so it never applies to an
    // interim level; the name list covers that case.
    const isAdult: boolean =
      serviceLevel !== null && adultLevel !== null
        ? serviceLevel >= adultLevel
        : ADULT_NAMES.has(upperName);
    return Object.freeze({
      isAdult,
      isHidden: entry.isHidden === true,
      isNotRated: NOT_RATED_NAMES.has(upperName),
      level,
      name,
      ratingTypeId,
      ratingValueId
    });
  }

  /**
   * A stored value list, or the shared empty list.
   * @param list - Stored list, if any
   * @returns The list, never undefined
   */
  private static valuesOrEmpty(
    list: ReadonlyArray<TRatingValueInfo> | undefined
  ): ReadonlyArray<TRatingValueInfo> {
    return list !== undefined ? list : NO_VALUES;
  }

  /**
   * Interim level from the name table.
   * @param ratingTypeId - Normalised rating type ID
   * @param upperName - Upper-cased display name
   * @returns The level, or null when the name is not in the table
   */
  private static interimLevel(
    ratingTypeId: string,
    upperName: string
  ): number | null {
    const table: Readonly<Record<string, number>> | undefined =
      INTERIM_LEVELS[ratingTypeId];
    if (
      table === undefined ||
      !Object.prototype.hasOwnProperty.call(table, upperName)
    ) {
      return null;
    }
    return table[upperName];
  }

  /**
   * Default unrated policy delivered for a rating type.
   * @param ratingTypeId - Rating type ID (any form)
   * @returns True to block, false to allow, null when not delivered
   */
  public getBlockUnrated(ratingTypeId: string): boolean | null {
    const type: TRatingTypeInfo | null = this.getRatingType(ratingTypeId);
    return type !== null ? type.blockUnratedPrograms : null;
  }

  /**
   * Values of a type with a known level, least restrictive first. Hidden
   * values are included; values with no level are not.
   * @param ratingTypeId - Rating type ID (any form)
   * @returns Ordered values, or an empty list
   */
  public getOrderedValues(
    ratingTypeId: string
  ): ReadonlyArray<TRatingValueInfo> {
    return RatingTaxonomy.valuesOrEmpty(
      this.mOrderedValues.get(RatingTaxonomy.normalizeId(ratingTypeId))
    );
  }

  /**
   * Look up a declared rating type.
   * @param ratingTypeId - Rating type ID (any form)
   * @returns The type, or null when it is not declared in `ratingType[]`
   */
  public getRatingType(ratingTypeId: string): TRatingTypeInfo | null {
    const type: TRatingTypeInfo | undefined = this.mTypes.get(
      RatingTaxonomy.normalizeId(ratingTypeId)
    );
    return type !== undefined ? type : null;
  }

  /**
   * Declared rating types in selector order: by `displayRank`, then response
   * order (WEB-PC-012, WEB-PC-044).
   * @returns Ordered types
   */
  public getRatingTypes(): ReadonlyArray<TRatingTypeInfo> {
    return this.mOrderedTypes;
  }

  /**
   * Rating Limit selector options for a type: ordered by level, hidden
   * values removed (WEB-PC-044).
   * @param ratingTypeId - Rating type ID (any form)
   * @returns Ordered options, or an empty list
   */
  public getSelectableValues(
    ratingTypeId: string
  ): ReadonlyArray<TRatingValueInfo> {
    return RatingTaxonomy.valuesOrEmpty(
      this.mSelectableValues.get(RatingTaxonomy.normalizeId(ratingTypeId))
    );
  }

  /**
   * Look up a rating value.
   * @param rating - Rating type/value pair (any ID form)
   * @returns Resolved facts, or null when the pair is not in the taxonomy
   */
  public getValue(rating: TContentRating): TRatingValueInfo | null {
    const info: TRatingValueInfo | undefined = this.mValues.get(
      RatingTaxonomy.toKey(
        RatingTaxonomy.normalizeId(rating.ratingTypeId),
        RatingTaxonomy.normalizeId(rating.ratingValueId)
      )
    );
    return info !== undefined ? info : null;
  }

  /**
   * Whether the taxonomy delivers a rating type, either declared in
   * `ratingType[]` or referenced by a value (WEB-PC-049).
   * @param ratingTypeId - Rating type ID (any form)
   * @returns True when delivered
   */
  public hasRatingType(ratingTypeId: string): boolean {
    return this.mDeliveredTypeIds.has(RatingTaxonomy.normalizeId(ratingTypeId));
  }

  /**
   * Normalise a rating ID: trim, and map a `type:` prefix to `tivo:` (same
   * rule as the existing rating-name lookup in data-domain-lib).
   * @param id - Raw ID
   * @returns Normalised ID, or an empty string for non-string input
   */
  public static normalizeId(id: unknown): string {
    if (typeof id !== "string" && typeof id !== "number") {
      return "";
    }
    return String(id)
      .trim()
      .replace(/^type:/i, "tivo:");
  }

  /**
   * Map key for a normalised type/value pair.
   * @param typeId - Normalised rating type ID
   * @param valueId - Normalised rating value ID
   * @returns Composite key
   */
  public static toKey(typeId: string, valueId: string): string {
    return `${typeId}|${valueId}`;
  }
}
